import * as React from 'react';
import { useFormatDateByUserProfileLocation } from '@leagueplatform/web-common';
import {
  Box,
  StackLayout,
  StatusBadge,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { getValueUnitDisplayLabel } from '#utils/get-value-unit-display-label.ts';
import {
  MasonryChartAggregationBucketType,
  MasonryChartInterval,
} from '#types/masonry-chart.type.ts';
import { ChartTooltip } from '../chart-tooltip';
import { HealthMetricDataV3 } from '../../range-graph/utils/use-health-metric-values';
import {
  bucketDateFormatMap,
  intervalDateFormatMap,
} from './utils/interval-format-map';

type SingleMeasurementTooltipV3Props = {
  datum: HealthMetricDataV3[number];
  interval: MasonryChartInterval;
  aggregationBucketType?: MasonryChartAggregationBucketType;
};

export function SingleMeasurementTooltipV3({
  datum,
  interval,
  aggregationBucketType,
}: SingleMeasurementTooltipV3Props) {
  const formatDateWithUserProfileLocation =
    useFormatDateByUserProfileLocation();

  const dateFormat = aggregationBucketType
    ? bucketDateFormatMap[aggregationBucketType]
    : intervalDateFormatMap[interval];

  return (
    <ChartTooltip>
      <StackLayout spacing="$quarter">
        <UtilityText
          size="xs"
          emphasis="subtle"
          css={{ typography: '$overline' }}
        >
          {datum.aggregationStrategyLabel
            ? datum.aggregationStrategyLabel
            : datum.metric.data.name}
        </UtilityText>
        <StackLayout orientation="horizontal" spacing="$half">
          {getValueUnitDisplayLabel({
            value: datum.value,
            label: datum.label,
            unitOptions: datum.metric.data.unit,
          }).map(([displayValue, displayUnit], index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box key={`${index} + ${datum.type}`}>
              <UtilityText as="span" css={{ typography: '$headingOne' }}>
                {displayValue}
              </UtilityText>{' '}
              <UtilityText emphasis="subtle" css={{ typography: '$bodyOne' }}>
                {displayUnit}
              </UtilityText>
            </Box>
          ))}
        </StackLayout>
        {datum.indicator ? (
          <StatusBadge
            showLight={false}
            label={datum.indicator.label}
            status={datum.indicator.style}
          />
        ) : null}
        <UtilityText size="eyebrow">
          {formatDateWithUserProfileLocation(datum.timestamp, dateFormat)}
        </UtilityText>
      </StackLayout>
    </ChartTooltip>
  );
}
